import {HttpClient, HttpResponse} from '@angular/common/http';
import {Inject, Injectable} from '@angular/core';
import {defaultOptions, responseToObject} from '@emma-labs/shared/angular';
import {
  ENVIRONMENT,
  EnvironmentBroker,
  objectToJson,
  PASSWORD_TOKEN_PARAM_KEY,
  Profile,
  PublicProfile,
  UserRole,
} from '@emma-labs/shared/typescript';
import {catchError, Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {PasswordUpdateRequest} from '../ui/profile-page/password/password-update-request';
import {handleHttpError} from '../utils/utility-constants';
import {AuthService} from './auth.service';

@Injectable({
  providedIn: 'root',
})
export class AgentApiService {
  basePath: string;
  nextGenBasePath: string;

  constructor(
    private readonly http: HttpClient,
    private readonly authService: AuthService,
    @Inject(ENVIRONMENT) readonly environment: EnvironmentBroker,
  ) {
    this.basePath = `${environment.apiBase}/agents`;
    this.nextGenBasePath = `${environment.msProposalApiBase}`;
  }

  get(agentId: string, sessionId: string): Observable<Profile> {
    const options = defaultOptions(sessionId).buildOptions();
    const url = new URL(`${this.basePath}/${agentId}/profile`);

    return this.http.get(url.toString(), options).pipe(
      map((response) => responseToObject<Profile>(response as HttpResponse<string>)),
      catchError((error) => handleHttpError(error, () => this.authService.handleLogout())),
    );
  }

  update(agentId: string, sessionId: string, profile: Profile): Observable<Profile> {
    const body = objectToJson(profile);
    const options = defaultOptions(sessionId).buildOptions();
    const url = new URL(`${this.basePath}/${agentId}/profile`);

    return this.http.put(url.toString(), body, options).pipe(
      map((response) => responseToObject<Profile>(response as HttpResponse<string>)),
      catchError((error) => handleHttpError(error, () => this.authService.handleLogout())),
    );
  }

  updatePassword(
    sessionId: string,
    agentId: string,
    passwordUpdateRequest: PasswordUpdateRequest,
  ): Observable<undefined> {
    const body = objectToJson(passwordUpdateRequest);
    const options = defaultOptions(sessionId).buildOptions();
    const url = new URL(`${this.basePath}/${agentId}/update-password`);

    return this.http.put(url.toString(), body, options).pipe(
      map(() => undefined),
      catchError((error) => handleHttpError(error, () => this.authService.handleLogout())),
    );
  }

  updatePasswordWithToken(passwordUpdateRequest: PasswordUpdateRequest): Observable<undefined> {
    const body = objectToJson(passwordUpdateRequest);
    const options = defaultOptions('').buildOptions();
    const url = new URL(`${this.basePath}/update-password`);

    return this.http.put(url.toString(), body, options).pipe(
      map(() => undefined),
      catchError((error) => handleHttpError(error, () => this.authService.handleLogout())),
    );
  }

  validatePasswordToken(passwordToken: string): Observable<boolean> {
    const body = objectToJson({[PASSWORD_TOKEN_PARAM_KEY]: passwordToken});
    const options = defaultOptions('').buildOptions();
    const url = new URL(`${this.basePath}/validate-password-token`);

    return this.http.post(url.toString(), body, options).pipe(
      map((response) => response.body as boolean),
      catchError((error) => handleHttpError(error, () => this.authService.handleLogout())),
    );
  }

  initiatePasswordReset(email: string): Observable<undefined> {
    const body = objectToJson({email});
    const options = defaultOptions('').buildOptions();
    const url = new URL(`${this.basePath}/forgot-password`);

    return this.http.post(url.toString(), body, options).pipe(
      map(() => undefined),
      catchError((error) => handleHttpError(error, () => this.authService.handleLogout())),
    );
  }

  getAgentsFromIds(
    sessionId: string,
    agentIds: string[],
  ): Observable<{id: string; insurerCode: string; fullName: string}[]> {
    const body = objectToJson({agentIds});
    const options = defaultOptions(sessionId).buildOptions();
    const url = new URL(`${this.basePath}/search`);

    return this.http.post(url.toString(), body, options).pipe(
      map((response) =>
        responseToObject<{id: string; humaniaCode: string; profile: {firstName: string; lastName: string}}[]>(
          response as HttpResponse<string>,
        ),
      ),
      map((agents) =>
        agents.map((agent) => ({
          id: atob(agent.id),
          insurerCode: agent.humaniaCode,
          fullName: `${agent.profile.firstName} ${agent.profile.lastName}`,
        })),
      ),
      catchError((error) => handleHttpError(error, () => this.authService.handleLogout())),
    );
  }

  getPublicProfilesByRoles(sessionId: string, roles: UserRole[]): Observable<PublicProfile[]> {
    const options = defaultOptions(sessionId).buildOptions();
    const url = new URL(`${this.basePath}/public-profiles`);
    roles.forEach((role) => url.searchParams.append('roles', role));

    return this.http.get(url.toString(), options).pipe(
      map((response) => responseToObject<PublicProfile[]>(response as HttpResponse<string>)),
      catchError((error) => handleHttpError(error, () => this.authService.handleLogout())),
    );
  }

  getPublicProfile(agentId: string, sessionId: string): Observable<PublicProfile> {
    const options = defaultOptions(sessionId).buildOptions();
    const url = new URL(`${this.basePath}/${btoa(agentId)}/public-profile`);

    return this.http.get(url.toString(), options).pipe(
      map((response) => responseToObject<PublicProfile>(response as HttpResponse<string>)),
      catchError((error) => handleHttpError(error, () => this.authService.handleLogout())),
    );
  }
}
